import React, { useState, useEffect } from 'react';
import { 
  SeasonMealPlan, SeasonalDayMeal, SEASONS_LIST, getCurrentSeasonId, DEFAULT_SEASONAL_PLANS,
  getLocalizedSeasonMetadata
} from '../lib/seasonalMealData';
import { sendWorkerAiRequest } from '../lib/workerAi';
import { DailyRecord, FoodItem } from '../types';
import { getStoredUser, apiGet, apiPost, apiPut } from '../lib/api';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import VoiceInputButton from './VoiceInputButton';
import { 
  getLocalizedFoodItem, getLocalizedFoodText, getLocalizedDayName, 
  getLocalizedSeasonName, getLocalizedSeasonMonths, getLocalizedDifficulty,
  getLocalizedShoppingItem, getLocalizedHabit
} from '../lib/foodTranslations';

// Icons
import { 
  Flame, Sparkles, Calendar, Edit3, CheckCircle2, RotateCcw, 
  ShoppingBag, Info, X, Layers, Coffee, Utensils, Clock, Heart, Copy, Plus, Trash2, CheckSquare, Square, Apple
} from 'lucide-react';

export interface HealthHabit {
  id: string;
  title: string;
  icon: string;
  shortDesc: string;
  benefits: string[];
  howToPrepare?: string;
  calories?: number;
  protein?: number;
  isCustom?: boolean;
}

export const DEFAULT_HEALTH_HABITS: HealthHabit[] = [
  {
    id: 'habit_coffee_glp1',
    title: 'قهوه سلامتی GLP-1 (کولد برو / اسپرسو + گردو و چیا)',
    icon: '☕',
    shortDesc: 'نوشیدنی گوارا و انرژی‌بخش برای تحریک حداکثری هورمون‌های GLP-1 و GIP (کنترل عمیق اشتها)',
    benefits: [
      'تحریک ۳ برابری ترشح هورمون‌های GLP-1 و GIP جهت ایجاد سیری عمیق و طولانی‌مدت',
      'تامین آنتی‌اکسیدان‌های قوی (کلروژنیک اسید) برای پیشگیری از التهاب سلولی',
      'تثبیت قند خون و جلوگیری از رهاسازی ناگهانی انسولین',
      'افزایش ۲۰۰۰ برابری جذب کورکومین به کمک ترکیب زردچوبه، فلفل سیاه و روغن زیتون'
    ],
    howToPrepare: '۲۰۰ میلی‌لیتر قهوه سرد (کولد برو یا ۲ شات اسپرسو + آب یخ) + ۱۵۰ گرم ماست یونانی + ۱۰ گرم گردو خردشده + ۱ قاشق چای‌خوری دانه چیا + ۱ پنس زردچوبه + ۱ پنس فلفل سیاه + ۵ میلی‌لیتر روغن زیتون فرابکر.',
    calories: 210,
    protein: 16
  },
  {
    id: 'habit_fiber_starter',
    title: 'پیش‌غذای فیبرمحور (سبزی خوردن شاهی / سالاد شیرازی)',
    icon: '🥗',
    shortDesc: 'مصرف شاهی، جعفری یا سالاد شیرازی با روغن زیتون ۱۰ دقیقه قبل از وعده اصلی',
    benefits: [
      'ایجاد پوشش فیبر نامحلول در معده و کند کردن سرعت جذب قند غذا',
      'ترک‌های تلخ (شاهی و جعفری) پمپ طبیعی هورمون GLP-1 را فعال می‌کنند',
      'کاهش حجم وعده اصلی و پیشگیری از سنگینی بعد از غذا'
    ]
  },
  {
    id: 'habit_no_sugar',
    title: 'حذف کامل قندهای مصنوعی و نوشیدنی‌های شیرین',
    icon: '🚫',
    shortDesc: 'جایگزینی چای و قهوه شیرین با گزینه‌های تلخ طبیعی و بدون قند',
    benefits: [
      'جلوگیری از اسپایک ناگهانی انسولین و ذخیره‌سازی چربی شکمی',
      'حفظ پایداری انرژی و پیشگیری از گرسنگی کاذب نیم‌ساعت بعد از نوشیدنی'
    ]
  },
  {
    id: 'habit_golden_mix',
    title: 'ترکیب طلایی (زردچوبه + فلفل سیاه + روغن زیتون)',
    icon: '✨',
    shortDesc: 'پاشیدن یک نمکدان از ترکیب پودر زردچوبه و فلفل روی تمامی غذاها و سالادها',
    benefits: [
      'افزایش ۲۰۰۰ برابری جذب کورکومین جهت سرکوب التهاب‌های مزمن متابولیک',
      'کنترل قوی اشتها و حمایت از کبد چرب و سلامت گوارش'
    ]
  },
  {
    id: 'habit_water_hydrate',
    title: 'هیدراتاسیون و مصرف ۸ لیوان آب سالم در طول روز',
    icon: '💧',
    shortDesc: 'نوشیدن آب کافی همراه با قطره لیموترش تازه بین وعده‌ها',
    benefits: [
      'تسهیل دفع سموم متابولیک و سوخت‌وساز بهتر چربی‌ها',
      'جلوگیری از اشتباه گرفتن تشنگی با احساس گرسنگی کاذب'
    ]
  }
];

interface Props {
  record?: DailyRecord;
  onUpdateRecord?: (record: DailyRecord) => void;
  onNavigateToShopping?: () => void;
}

export default function SeasonalMealPlanModule({ record, onUpdateRecord }: Props) {
  const { t, lang } = useLanguage();
  const toPersianDigits = (num: number | string) => formatNumberByLang(num, lang);
  const [selectedSeasonId, setSelectedSeasonId] = useState<'spring' | 'summer' | 'autumn' | 'winter'>(getCurrentSeasonId());
  const [activeTab, setActiveTab] = useState<'grid' | 'habits' | 'shopping' | 'summary' | 'nutrition' | 'tips'>('grid');
  const [selectedWeekFilter, setSelectedWeekFilter] = useState<number | 'all'>('all');
  const [mealTypeFilter, setMealTypeFilter] = useState<'all' | 'lunch' | 'snack'>('all');

  // Meal Plan & Habits State stored per user in Cloudflare D1
  const [plans, setPlans] = useState<Record<'spring' | 'summer' | 'autumn' | 'winter', SeasonMealPlan>>(DEFAULT_SEASONAL_PLANS);
  const [completedHabits, setCompletedHabits] = useState<Record<string, boolean>>({});
  const [customHabits, setCustomHabits] = useState<HealthHabit[]>([]);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  
  const [isLoadingDb, setIsLoadingDb] = useState<boolean>(true);
  const [isSavingDb, setIsSavingDb] = useState<boolean>(false);

  // New Habit Modal
  const [newHabitModalOpen, setNewHabitModalOpen] = useState<boolean>(false);
  const [newHabitTitle, setNewHabitTitle] = useState<string>('');
  const [newHabitDesc, setNewHabitDesc] = useState<string>('');
  const [newHabitBenefit, setNewHabitBenefit] = useState<string>('');

  // Editing Day Modal state
  const [editingDay, setEditingDay] = useState<SeasonalDayMeal | null>(null);

  // AI Modal state
  const [aiModalOpen, setAiModalOpen] = useState<boolean>(false);
  const [aiTargetDay, setAiTargetDay] = useState<SeasonalDayMeal | null>(null);
  const [aiUserPrompt, setAiUserPrompt] = useState<string>('');
  const [aiResponse, setAiResponse] = useState<string>('');
  const [isAiLoading, setIsAiLoading] = useState<boolean>(false);

  // Toast notification
  const [toastText, setToastText] = useState<string | null>(null);

  const currentSeason = plans[selectedSeasonId] || DEFAULT_SEASONAL_PLANS[selectedSeasonId];
  const localizedMeta = getLocalizedSeasonMetadata(selectedSeasonId, lang);
  const seasonName = getLocalizedSeasonName(selectedSeasonId, lang);
  const focusTitle = localizedMeta.focusTitle || currentSeason.focusTitle;
  const description = localizedMeta.description || currentSeason.description;
  const monthlySummary = localizedMeta.monthlySummary || currentSeason.monthlySummary;
  const nutritionalAnalysis = localizedMeta.nutritionalAnalysis || currentSeason.nutritionalAnalysis;
  const timeSavingTips = localizedMeta.timeSavingTips || currentSeason.timeSavingTips;

  const realCurrentSeasonId = getCurrentSeasonId();

  const showToast = (msg: string) => {
    setToastText(msg);
    setTimeout(() => setToastText(null), 3000);
  };

  // D1 PERSISTENCE — load seasonal meal plan on mount from local server REST API
  useEffect(() => {
    const user = getStoredUser();
    if (!user) {
      setIsLoadingDb(false);
      return;
    }

    setIsLoadingDb(true);
    apiGet<any[]>('/api/meal-plans')
      .then(res => {
        if (res.success && Array.isArray(res.data)) {
          // Find user specific plan first, or legacy packed plan
          const userPlan = res.data.find(p => 
            p.user_id && (p.plan_data || (p.plan_name && p.plan_name.startsWith('seasonal_allSeasons__')))
          ) || res.data.find(p => p.plan_data || (p.plan_name && p.plan_name.startsWith('seasonal_allSeasons__')));

          if (userPlan) {
            setCurrentPlanId(userPlan.id);
            try {
              let data: any = null;
              if (userPlan.plan_data) {
                data = typeof userPlan.plan_data === 'string' ? JSON.parse(userPlan.plan_data) : userPlan.plan_data;
              } else if (userPlan.plan_name && userPlan.plan_name.startsWith('seasonal_allSeasons__')) {
                const rawJson = userPlan.plan_name.substring('seasonal_allSeasons__'.length);
                data = JSON.parse(rawJson);
              }
              if (data) {
                if (data.plans) setPlans((prev: any) => ({ ...prev, ...data.plans }));
                if (data.completedHabits) setCompletedHabits(data.completedHabits);
                if (data.customHabits) setCustomHabits(data.customHabits);
              }
            } catch (pErr) {
              console.warn('Failed parsing seasonal meal plan data:', pErr);
            }
          }
        }
      })
      .catch(err => {
        console.warn('Local REST seasonal meal plan fetch error:', err);
        showToast(t('seasonalMealPlanFetchError', 'خطا در بارگذاری برنامه‌های غذایی از سرور'));
      })
      .finally(() => setIsLoadingDb(false));
  }, [t]);

  // Save current plans & habits to local server REST API
  const savePlansToCloud = async (
    updatedPlans: Record<'spring' | 'summer' | 'autumn' | 'winter', SeasonMealPlan>,
    updatedHabits?: Record<string, boolean>,
    updatedCustomHabits?: HealthHabit[]
  ) => {
    const user = getStoredUser();
    setIsSavingDb(true);

    try {
      if (user) {
        const payload = {
          plans: updatedPlans,
          completedHabits: updatedHabits !== undefined ? updatedHabits : completedHabits,
          customHabits: updatedCustomHabits !== undefined ? updatedCustomHabits : customHabits,
          updatedAt: new Date().toISOString(),
          userEmail: user.email
        };

        const planName = 'برنامه غذایی فصلی و ماهانه GLP-1';
        const planDesc = 'برنامه غذایی شخصی‌سازی‌شده و پیگیری عادات سلامتی';

        let targetId = currentPlanId;

        // If no cached ID, query D1 first
        if (!targetId) {
          const checkRes = await apiGet<any[]>('/api/meal-plans');
          if (checkRes.success && Array.isArray(checkRes.data)) {
            const matched = checkRes.data.find(p => p.user_id);
            if (matched) {
              targetId = matched.id;
              setCurrentPlanId(matched.id);
            }
          }
        }

        if (targetId) {
          // Update existing user plan in D1
          const updateRes = await apiPut<any>(`/api/meal-plans/${targetId}`, {
            plan_name: planName,
            season: 'all',
            description: planDesc,
            plan_data: JSON.stringify(payload),
            is_active: true
          });
          if (!updateRes.success) {
            throw new Error(updateRes.error || 'Failed to update plan in D1');
          }
        } else {
          // Create new user plan in D1
          const createRes = await apiPost<any>('/api/meal-plans', {
            plan_name: planName,
            season: 'all',
            description: planDesc,
            plan_data: JSON.stringify(payload),
            is_active: true
          });
          if (createRes.success && createRes.data?.id) {
            setCurrentPlanId(createRes.data.id);
          } else {
            throw new Error(createRes.error || 'Failed to create plan in D1');
          }
        }

        showToast(t('seasonalMealPlanSavedSuccess', 'تغییرات با موفقیت ذخیره شد ✨'));
      } else {
        showToast(t('seasonalMealPlanUpdatedLocally', 'برنامه بروزرسانی شد (جهت ذخیره ابری وارد حساب شوید)'));
      }
    } catch (err: any) {
      console.error('Failed to save seasonal meal plan to D1:', err);
      showToast(t('seasonalMealPlanSaveError', 'خطا در ذخیره‌سازی در پایگاه داده D1'));
    } finally {
      setIsSavingDb(false);
    }
  };

  // Toggle Habit completion for today
  const handleToggleHabit = (habitId: string) => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const key = `${todayStr}_${habitId}`;
    const newHabits = {
      ...completedHabits,
      [key]: !completedHabits[key]
    };
    setCompletedHabits(newHabits);
    savePlansToCloud(plans, newHabits);
  };

  // Add custom habit
  const handleAddCustomHabit = () => {
    if (!newHabitTitle.trim()) return;
    const newHabit: HealthHabit = {
      id: 'custom_habit_' + Date.now(),
      title: newHabitTitle.trim(),
      icon: '🌱',
      shortDesc: newHabitDesc.trim() || t('seasonalMealPlanCustomHabitDesc', 'عادت سلامت شخصی کاربر'),
      benefits: newHabitBenefit.trim() ? [newHabitBenefit.trim()] : [t('seasonalMealPlanCustomHabitBenefit', 'افزایش انرژی و بهبود سلامتی متابولیک')],
      isCustom: true
    };

    const updatedCustom = [...customHabits, newHabit];
    setCustomHabits(updatedCustom);
    setNewHabitTitle('');
    setNewHabitDesc('');
    setNewHabitBenefit('');
    setNewHabitModalOpen(false);
    savePlansToCloud(plans, completedHabits, updatedCustom);
    showToast(t('seasonalMealPlanCustomHabitAdded', 'عادت جدید با موفقیت اضافه شد ✨'));
  };

  // Remove custom habit
  const handleRemoveCustomHabit = (habitId: string) => {
    const updatedCustom = customHabits.filter(h => h.id !== habitId);
    setCustomHabits(updatedCustom);
    savePlansToCloud(plans, completedHabits, updatedCustom);
    showToast(t('seasonalMealPlanCustomHabitRemoved', 'عادت شخصی حذف شد'));
  };

  // Update a single day meal (Lunch & Snack)
  const handleSaveDayEdit = (updatedDay: SeasonalDayMeal) => {
    const newSeasonDays = currentSeason.days.map(d => d.dayId === updatedDay.dayId ? updatedDay : d);
    const updatedSeason: SeasonMealPlan = {
      ...currentSeason,
      days: newSeasonDays
    };

    const newPlans = {
      ...plans,
      [selectedSeasonId]: updatedSeason
    };

    setPlans(newPlans);
    setEditingDay(null);
    savePlansToCloud(newPlans);
  };

  // Reset current season plan to default
  const handleResetSeasonToDefault = () => {
    if (confirm(t('seasonalMealPlanResetConfirm', 'آیا از بازنشانی برنامه {{seasonName}} به حالت استاندارد اولیه اطمینان دارید؟', { seasonName: seasonName }))) {
      const newPlans = {
        ...plans,
        [selectedSeasonId]: DEFAULT_SEASONAL_PLANS[selectedSeasonId]
      };
      setPlans(newPlans);
      savePlansToCloud(newPlans);
      showToast(t('seasonalMealPlanResetSuccess', 'برنامه {{seasonName}} به منوی اولیه بازنشانی شد', { seasonName: seasonName }));
    }
  };

  // Log Lunch or Snack to Today's Record
  const handleLogMealToTodayRecord = (dayMeal: SeasonalDayMeal, mealType: 'lunch' | 'snack') => {
    if (!record || !onUpdateRecord) {
      showToast(t('seasonalMealPlanDailyRecordError', 'خطا: ثبت روزانه فعال نیست'));
      return;
    }

    const now = new Date();
    const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    const isSnack = mealType === 'snack';
    const rawFoodTitle = isSnack 
      ? (dayMeal.snackName || t('seasonalMealPlanDefaultSnackName', 'قهوه سلامتی + ماست یونانی و گردو'))
      : dayMeal.foodName;
    const rawPortion = isSnack 
      ? (dayMeal.snackPortionSize || t('seasonalMealPlanDefaultSnackPortion', '۱ لیوان (۲۵۰ml)')) 
      : dayMeal.portionSize;

    const locItem = getLocalizedFoodItem(rawFoodTitle, rawPortion, lang);

    const newFoodItem: FoodItem = {
      id: 'seasonal-food-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
      name: `${locItem.name} (${isSnack ? t('seasonalMealPlanSnackType', 'میان‌وعده') : t('seasonalMealPlanLunchType', 'ناهار')} - ${seasonName})`,
      category: isSnack ? 'Snack' : 'Lunch',
      quantity: locItem.quantity,
      calories: isSnack ? (dayMeal.snackCalories || 210) : dayMeal.calories,
      protein: isSnack ? (dayMeal.snackProtein || 16) : dayMeal.protein,
      carbs: isSnack ? (dayMeal.snackCarbs || 12) : dayMeal.carbs,
      fat: isSnack ? (dayMeal.snackFat || 10) : dayMeal.fat,
      time: timeStr
    };

    const updatedFoods = [...(record.foods || []), newFoodItem];
    onUpdateRecord({
      ...record,
      foods: updatedFoods
    });

    showToast(t('seasonalMealPlanMealLogged', 'وعده "{{foodTitle}}" در {{mealType}} امروز ثبت شد 🍲', { foodTitle: locItem.name, mealType: isSnack ? t('seasonalMealPlanSnackType', 'میان‌وعده') : t('seasonalMealPlanLunchType', 'ناهار') }));
  };

  // Copy text helper
  const handleCopyText = (text: string, title: string) => {
    navigator.clipboard.writeText(text);
    showToast(t('seasonalMealPlanCopied', '{{title}} کپی شد!', { title }));
  };

  // AI ASSISTANT FOR SEASONAL MEAL PLAN
  const sanitizePersianAiOutput = (rawText: string): string => {
    if (!rawText) return '';
    let cleaned = rawText
      .replace(/Inflamation/gi, 'التهاب')
      .replace(/type\s*2|type\s*۲/gi, 'نوع ۲')
      .replace(/máximo|maximo/gi, 'حداکثر')
      .replace(/добавهای|добавки/gi, 'افزودنی‌های')
      .replace(/filtr/gi, 'صاف‌شده (فیلترشده)')
      .replace(/مغذیات/gi, 'ارزش غذایی')
      .replace(/portion/gi, 'وعده')
      .replace(/냉\s*brew|cold\s*brew/gi, 'قهوه سرد (کولد برو)')
      .replace(/chutney|چुटنی/gi, 'مقدار بسیار کم (یک پنس)')
      .replace(/ligeramente\s*spumoso\s*obtenga/gi, 'کمی کف‌دار و یکدست شود')
      .replace(/konsistency\s*cremyl/gi, 'بافت کرمی')
      .replace(/contacting\s*with\s*liquid/gi, 'تماس با مایعات')
      .replace(/effet\s*sinérgico\s*de\s*aumento\s*de\s*GLP-1/gi, 'تأثیر هم‌افزایی در افزایش هورمون GLP-1')
      .replace(/libération\s*lenta\s*de\s*glukose/gi, 'رهاسازی تدریجی قند خون')
      .replace(/spikes\s*glicémiques/gi, 'نوسانات ناگهانی قند خون')
      .replace(/völlig\s*fein/gi, 'کاملاً خرد شده')
      .replace(/recién\s*خرد/gi, 'تازه آسیاب شده')
      .replace(/زردچوبه-gray/gi, 'پودر زردچوبه')
      .replace(/혼합\s*및\s*서빙/gi, 'مخلوط کردن و سرو')
      .replace(/几块冰/gi, 'چند قالب یخ')
      .replace(/منعش/gi, 'گوارا و باطراوت');

    cleaned = cleaned.replace(/[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uac00-\ud7af\u0400-\u04FF]/g, '');
    return cleaned;
  };

  const handleRunAiAssistant = async () => {
    if (!aiUserPrompt.trim() && !aiTargetDay) return;
    setIsAiLoading(true);
    setAiResponse('');

    try {
      const locTargetFood = aiTargetDay ? getLocalizedFoodItem(aiTargetDay.foodName, aiTargetDay.portionSize, lang) : null;
      const targetFoodText = locTargetFood ? `${locTargetFood.name} (${getLocalizedFoodText(aiTargetDay?.ingredients || '', lang)})` : '';
      const promptLang = lang === 'sv' ? 'Svenska (Swedish)' : lang === 'en' ? 'English' : 'Persian (Farsi)';
      const promptText = `
You are a senior clinical nutritionist and metabolic health specialist.
User Language: ${promptLang}. Please answer 100% in ${promptLang}.
Season: ${seasonName}
${locTargetFood ? `Food to optimize: ${targetFoodText} - Portion: ${locTargetFood.quantity}` : ''}
User Query: "${aiUserPrompt}"
5 Golden Metabolic Rules:
1. Healthy Fats (Olive oil, walnuts, Greek yogurt) -> GLP-1 hormone stimulation
2. High Protein (Chicken, lean meat, fish, legumes) -> Long-lasting satiety
3. High Fiber & Bitter herbs (Watercress, parsley, spinach) -> Natural GLP-1 pump
4. Golden Synergy: Turmeric + black pepper + olive oil -> High bioavailability curcumin
5. Start meals with fiber starter salad.

Please provide an expert, practical advice and recipe with nutritional values (calories, protein, carbs, fat) in ${promptLang}.
`;

      const res = await sendWorkerAiRequest('nutrition', promptText);
      const cleanedRes = lang === 'fa' ? sanitizePersianAiOutput(res) : res;
      setAiResponse(cleanedRes);
    } catch (err) {
      console.error('AI assistant request failed:', err);
      setAiResponse(t('seasonalMealPlanAiError', 'متأسفانه در دریافت پاسخ از هوش مصنوعی خطایی رخ داد. لطفاً دوباره تلاش کنید.'));
    } finally {
      setIsAiLoading(false);
    }
  };

  // Filtered days for week selection
  const filteredDays = currentSeason.days.filter(d => {
    if (selectedWeekFilter === 'all') return true;
    if (selectedWeekFilter === 1) return d.weekNum === 1 || d.weekNum === 3;
    if (selectedWeekFilter === 2) return d.weekNum === 2 || d.weekNum === 4;
    return d.weekNum === selectedWeekFilter;
  });

  const todayStr = new Date().toISOString().slice(0, 10);
  const allHabitsList = [...DEFAULT_HEALTH_HABITS, ...customHabits];

  return (
    <div dir={lang === 'fa' ? 'rtl' : 'ltr'} className="space-y-6 pb-12 font-sans">
      {/* Toast Banner */}
      {toastText && (
        <div className="fixed top-20 right-4 z-50 bg-gray-900 text-white dark:bg-emerald-600 px-4 py-3 rounded-2xl shadow-xl flex items-center gap-3 animate-in fade-in slide-in-from-top duration-300">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 dark:text-white" />
          <span className="text-xs sm:text-sm font-bold">{toastText}</span>
        </div>
      )}

      {/* HEADER BANNER: GLP-1 Metabolic Health Concept */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-900 text-white p-6 sm:p-8 shadow-2xl border border-emerald-800/40">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 via-amber-300 to-teal-400" />
        <div className="absolute -left-10 -bottom-10 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -right-10 -top-10 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 bg-emerald-500/20 text-emerald-300 px-3 py-1 rounded-full text-xs font-bold border border-emerald-500/30">
              <Sparkles className="w-3.5 h-3.5 text-amber-300" />
              <span>{t('seasonalMealPlanHeaderSubtitle', 'برنامه فصلی تغذیه سالم و بهینه‌سازی متابولیک')}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setAiTargetDay(null);
                  setAiUserPrompt('');
                  setAiResponse('');
                  setAiModalOpen(true);
                }}
                className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-amber-500 to-emerald-500 hover:from-amber-400 hover:to-emerald-400 text-gray-950 font-bold text-xs flex items-center gap-1.5 shadow-md transition-all cursor-pointer"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>{t('seasonalMealPlanAiConsulting', 'مشاوره و بهینه‌سازی AI')}</span>
              </button>

              <button
                onClick={handleResetSeasonToDefault}
                className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold flex items-center gap-1 transition-all cursor-pointer"
                title={t('seasonalMealPlanResetTooltip', 'بازنشانی برنامه این فصل به حالت اولیه')}
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{t('seasonalMealPlanDefaultMenu', 'منوی اولیه')}</span>
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <h1 className="text-xl sm:text-3xl font-black text-white flex items-center gap-3">
              <span>{t('seasonalMealPlanHeaderTitle', 'برنامه غذایی ماهانه و فصلی تحریک هورمون GLP-1')}</span>
              <span className="text-2xl">{currentSeason.seasonIcon}</span>
            </h1>
            <p className="text-xs sm:text-sm text-emerald-100/90 max-w-3xl leading-relaxed">
              {t('seasonalMealPlanHeaderDesc', 'برنامه تغذیه هوشمند و بهینه‌سازی سلامت متابولیک (GLP-1) با ۵ ترفند کلیدی تغذیه سالم در چهار فصل سال.')}
            </p>
          </div>

          {/* 5 Golden Rules Pills */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 pt-2">
            {[
              { icon: '🥑', label: t('seasonalMealPlanRuleFatLabel', 'چربی مفید'), sub: t('seasonalMealPlanRuleFatSub', 'زیتون، گردو، ماست یونانی') },
              { icon: '🍗', label: t('seasonalMealPlanRuleProteinLabel', 'پروتئین بالا'), sub: t('seasonalMealPlanRuleProteinSub', 'مرغ، گوشت لخم، ماهی، حبوبات') },
              { icon: '🥗', label: t('seasonalMealPlanRuleFiberLabel', 'فیبر و طعم تلخ'), sub: t('seasonalMealPlanRuleFiberSub', 'شاهی، جعفری، بامیه، اسفناج') },
              { icon: '🟡', label: t('seasonalMealPlanRuleGoldenLabel', 'ترکیب طلایی'), sub: t('seasonalMealPlanRuleGoldenSub', 'زردچوبه + فلفل + روغن زیتون') },
              { icon: '🌿', label: t('seasonalMealPlanRuleStartLabel', 'استارت با فیبر'), sub: t('seasonalMealPlanRuleStartSub', 'شروع وعده با سالاد شیرازی') }
            ].map((rule, idx) => (
              <div key={idx} className="bg-white/10 dark:bg-black/20 backdrop-blur-md p-2.5 rounded-2xl border border-white/10 text-start space-y-0.5">
                <div className="flex items-center gap-1.5">
                  <span className="text-base">{rule.icon}</span>
                  <span className="text-xs font-bold text-emerald-200">{rule.label}</span>
                </div>
                <p className="text-[10px] text-gray-300 truncate">{rule.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SEASONS TAB SELECTOR */}
      <div className="flex items-center justify-between gap-2 overflow-x-auto pb-2 scrollbar-none">
        <div className="flex items-center gap-2">
          {SEASONS_LIST.map(s => {
            const isSelected = selectedSeasonId === s.id;
            const isCurrent = realCurrentSeasonId === s.id;
            const localizedSName = getLocalizedSeasonName(s.id, lang);

            return (
              <button
                key={s.id}
                onClick={() => setSelectedSeasonId(s.id)}
                className={`px-4 py-2.5 rounded-2xl font-bold text-xs sm:text-sm flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap shadow-sm ${
                  isSelected
                    ? 'bg-emerald-600 text-white shadow-emerald-600/30 shadow-md ring-2 ring-emerald-500 ring-offset-2 dark:ring-offset-gray-950'
                    : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-800'
                }`}
              >
                <span className="text-base">{s.icon}</span>
                <span>{localizedSName}</span>
                {isCurrent && (
                  <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black bg-amber-400 text-gray-950">
                    {t('seasonalMealPlanCurrentSeasonLabel', 'فصل جاری')}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="text-xs text-gray-500 dark:text-gray-400 font-medium hidden md:block">
          📅 {seasonName}: {getLocalizedSeasonMonths(selectedSeasonId, lang)}
        </div>
      </div>

      {/* MODULE VIEWS TABS */}
      <div className="bg-white dark:bg-gray-900 p-2 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1 overflow-x-auto w-full sm:w-auto">
          {[
            { id: 'grid', label: t('seasonalMealPlanTabGrid', 'برنامه ۳۰ روزه ناهار و میان‌وعده'), icon: Calendar },
            { id: 'habits', label: t('seasonalMealPlanTabHabits', 'عادات و توصیه‌های سلامت (قهوه)'), icon: Coffee },
            { id: 'shopping', label: t('seasonalMealPlanTabShopping', 'لیست خرید هفتگی'), icon: ShoppingBag },
            { id: 'summary', label: t('seasonalMealPlanTabSummary', 'خلاصه مواد ماهانه'), icon: Layers },
            { id: 'nutrition', label: t('seasonalMealPlanTabNutrition', 'تحلیل تغذیه‌ای'), icon: Heart },
            { id: 'tips', label: t('seasonalMealPlanTabTips', 'ترفندهای آشپزی'), icon: Clock }
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-3 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                  isActive
                    ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/60 dark:text-emerald-200 font-black'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <Icon className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Filters for Grid View */}
        {activeTab === 'grid' && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Meal Type Filter */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl text-xs">
              <span className="text-[11px] text-gray-500 px-1 font-bold">{t('seasonalMealPlanMealFilterLabel', 'وعده:')}</span>
              <button
                onClick={() => setMealTypeFilter('all')}
                className={`px-2.5 py-1 rounded-lg font-bold cursor-pointer transition-all ${
                  mealTypeFilter === 'all'
                    ? 'bg-white dark:bg-gray-900 text-emerald-600 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {t('seasonalMealPlanFilterAll', 'همه')}
              </button>
              <button
                onClick={() => setMealTypeFilter('lunch')}
                className={`px-2.5 py-1 rounded-lg font-bold cursor-pointer transition-all ${
                  mealTypeFilter === 'lunch'
                    ? 'bg-white dark:bg-gray-900 text-emerald-600 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {t('seasonalMealPlanFilterLunch', 'ناهار')}
              </button>
              <button
                onClick={() => setMealTypeFilter('snack')}
                className={`px-2.5 py-1 rounded-lg font-bold cursor-pointer transition-all ${
                  mealTypeFilter === 'snack'
                    ? 'bg-white dark:bg-gray-900 text-emerald-600 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {t('seasonalMealPlanFilterSnack', 'میان‌وعده')}
              </button>
            </div>

            {/* Week Filter */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl text-xs">
              <span className="text-[11px] text-gray-500 px-1 font-bold">{t('seasonalMealPlanWeekFilterLabel', 'هفته:')}</span>
              <button
                onClick={() => setSelectedWeekFilter('all')}
                className={`px-2.5 py-1 rounded-lg font-bold cursor-pointer transition-all ${
                  selectedWeekFilter === 'all'
                    ? 'bg-white dark:bg-gray-900 text-emerald-600 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {t('seasonalMealPlanFilterAll', 'همه')}
              </button>
              <button
                onClick={() => setSelectedWeekFilter(1)}
                className={`px-2.5 py-1 rounded-lg font-bold cursor-pointer transition-all ${
                  selectedWeekFilter === 1
                    ? 'bg-white dark:bg-gray-900 text-emerald-600 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {t('seasonalMealPlanFilterWeek13', 'هفته ۱ و ۳')}
              </button>
              <button
                onClick={() => setSelectedWeekFilter(2)}
                className={`px-2.5 py-1 rounded-lg font-bold cursor-pointer transition-all ${
                  selectedWeekFilter === 2
                    ? 'bg-white dark:bg-gray-900 text-emerald-600 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {t('seasonalMealPlanFilterWeek24', 'هفته ۲ و ۴')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* VIEW 1: 30-DAY MONTHLY MEAL GRID (LUNCH & SNACK) */}
      {activeTab === 'grid' && (
        <div className="space-y-4">
          <div className="bg-emerald-50/70 dark:bg-emerald-950/30 p-3.5 rounded-2xl border border-emerald-200/60 dark:border-emerald-800/60 text-xs text-emerald-900 dark:text-emerald-200 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span>
                <strong>{currentSeason.seasonName}:</strong> {currentSeason.focusTitle}. {t('seasonalMealPlanGridInfo', 'هر روز شامل ناهار و میان‌وعده سالم مغذی است.')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold bg-white dark:bg-gray-900 px-2.5 py-1 rounded-xl border border-emerald-200 dark:border-emerald-800">
                {t('seasonalMealPlanDaysCount', 'روزها: {{count}} روز', { count: toPersianDigits(filteredDays.length) })}
              </span>
            </div>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDays.map((dayMeal) => {
              const localizedDayName = getLocalizedDayName(dayMeal.dayName, lang);
              const localizedDiff = getLocalizedDifficulty(dayMeal.difficulty, lang);
              const locLunch = getLocalizedFoodItem(dayMeal.foodName, dayMeal.portionSize, lang);
              const locLunchIng = getLocalizedFoodText(dayMeal.ingredients, lang);
              const locLunchSide = getLocalizedFoodText(dayMeal.sideDishes, lang);

              const rawSnackName = dayMeal.snackName || 'قهوه سلامتی (کولد برو) + ماست یونانی و دانه چیا';
              const rawSnackPortion = dayMeal.snackPortionSize || '۱ لیوان (۲۵۰ml)';
              const rawSnackIng = dayMeal.snackIngredients || 'قهوه سرد + ۱۵۰گ ماست یونانی + ۱۰گ گردو + ۱ ق‌چ دانه چیا + زردچوبه و روغن زیتون';

              const locSnack = getLocalizedFoodItem(rawSnackName, rawSnackPortion, lang);
              const locSnackIng = getLocalizedFoodText(rawSnackIng, lang);

              const defaultSnackCal = dayMeal.snackCalories || 210;
              const defaultSnackProt = dayMeal.snackProtein || 16;
              const defaultSnackCarb = dayMeal.snackCarbs || 10;
              const defaultSnackFat = dayMeal.snackFat || 12;

              return (
                <div
                  key={dayMeal.dayId}
                  className="bg-white dark:bg-gray-900 rounded-3xl p-5 border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4 relative group"
                >
                  {/* Header info */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-2.5">
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-1 rounded-xl bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 font-bold text-xs">
                          {localizedDayName}
                        </span>
                        <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400">
                          {t('seasonalMealPlanWeekNum', 'هفته {{num}}', { num: toPersianDigits(dayMeal.weekNum) })}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold ${
                          dayMeal.difficulty === 'آسان'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                            : dayMeal.difficulty === 'متوسط'
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                            : 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'
                        }`}>
                          {localizedDiff}
                        </span>
                        <button
                          onClick={() => setEditingDay(dayMeal)}
                          className="p-1.5 text-gray-400 hover:text-emerald-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all cursor-pointer"
                          title={t('seasonalMealPlanEditMealsTitle', 'ویرایش ناهار و میان‌وعده')}
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* SECTION 1: LUNCH (ناهار) */}
                    {(mealTypeFilter === 'all' || mealTypeFilter === 'lunch') && (
                      <div className="space-y-2 bg-emerald-50/40 dark:bg-emerald-950/20 p-3 rounded-2xl border border-emerald-100 dark:border-emerald-900/40">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-black text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                            <Utensils className="w-3.5 h-3.5" />
                            {t('seasonalMealPlanLunchTitle', 'وعده ناهار')}
                          </span>
                          <span className="text-[10px] text-gray-500">{t('seasonalMealPlanPortionLabel', 'سهم:')} {locLunch.quantity}</span>
                        </div>
                        <h3 className="text-sm font-black text-gray-900 dark:text-white leading-tight">
                          {locLunch.name}
                        </h3>
                        <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-snug">
                          {locLunchIng}
                        </p>

                        {/* Lunch Macros */}
                        <div className="grid grid-cols-4 gap-1 text-center font-mono text-[10px] pt-1">
                          <div className="bg-amber-50 dark:bg-amber-950/40 p-1 rounded-lg border border-amber-200/50">
                            <span className="block text-[8px] text-gray-500">{t('seasonalMealPlanCalorie', 'کالری')}</span>
                            <strong className="text-amber-900 dark:text-amber-200">{toPersianDigits(dayMeal.calories)}</strong>
                          </div>
                          <div className="bg-emerald-50 dark:bg-emerald-950/40 p-1 rounded-lg border border-emerald-200/50">
                            <span className="block text-[8px] text-gray-500">{t('seasonalMealPlanProtein', 'پروتئین')}</span>
                            <strong className="text-emerald-900 dark:text-emerald-200">{toPersianDigits(dayMeal.protein)}g</strong>
                          </div>
                          <div className="bg-blue-50 dark:bg-blue-950/40 p-1 rounded-lg border border-blue-200/50">
                            <span className="block text-[8px] text-gray-500">{t('seasonalMealPlanCarb', 'کربو')}</span>
                            <strong className="text-blue-900 dark:text-blue-200">{toPersianDigits(dayMeal.carbs)}g</strong>
                          </div>
                          <div className="bg-rose-50 dark:bg-rose-950/40 p-1 rounded-lg border border-rose-200/50">
                            <span className="block text-[8px] text-gray-500">{t('seasonalMealPlanFat', 'چربی')}</span>
                            <strong className="text-rose-900 dark:text-rose-200">{toPersianDigits(dayMeal.fat)}g</strong>
                          </div>
                        </div>

                        {/* Side Dishes */}
                        <div className="text-[10px] text-emerald-800 dark:text-emerald-300 flex items-center gap-1 pt-1">
                          <span className="font-bold shrink-0">🌱 {t('seasonalMealPlanSideDish', 'دورچین:')}</span>
                          <span className="truncate">{locLunchSide}</span>
                        </div>
                      </div>
                    )}

                    {/* SECTION 2: SNACK (میان‌وعده) */}
                    {(mealTypeFilter === 'all' || mealTypeFilter === 'snack') && (
                      <div className="space-y-2 bg-amber-50/40 dark:bg-amber-950/20 p-3 rounded-2xl border border-amber-100 dark:border-amber-900/40">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-black text-amber-700 dark:text-amber-300 flex items-center gap-1">
                            <Coffee className="w-3.5 h-3.5" />
                            {t('seasonalMealPlanSnackTitle', 'وعده میان‌وعده')}
                          </span>
                          <span className="text-[10px] text-gray-500">{t('seasonalMealPlanPortionLabel', 'سهم:')} {locSnack.quantity}</span>
                        </div>
                        <h4 className="text-xs font-black text-gray-900 dark:text-white leading-tight">
                          {locSnack.name}
                        </h4>
                        <p className="text-[10px] text-gray-600 dark:text-gray-300 leading-snug">
                          {locSnackIng}
                        </p>

                        {/* Snack Macros */}
                        <div className="grid grid-cols-4 gap-1 text-center font-mono text-[10px] pt-1">
                          <div className="bg-amber-100/60 dark:bg-amber-900/40 p-1 rounded-lg">
                            <span className="block text-[8px] text-gray-500">{t('seasonalMealPlanCalorie', 'کالری')}</span>
                            <strong className="text-amber-900 dark:text-amber-200">{toPersianDigits(defaultSnackCal)}</strong>
                          </div>
                          <div className="bg-emerald-100/60 dark:bg-emerald-900/40 p-1 rounded-lg">
                            <span className="block text-[8px] text-gray-500">{t('seasonalMealPlanProtein', 'پروتئین')}</span>
                            <strong className="text-emerald-900 dark:text-emerald-200">{toPersianDigits(defaultSnackProt)}g</strong>
                          </div>
                          <div className="bg-blue-100/60 dark:bg-blue-900/40 p-1 rounded-lg">
                            <span className="block text-[8px] text-gray-500">{t('seasonalMealPlanCarb', 'کربو')}</span>
                            <strong className="text-blue-900 dark:text-blue-200">{toPersianDigits(defaultSnackCarb)}g</strong>
                          </div>
                          <div className="bg-rose-100/60 dark:bg-rose-900/40 p-1 rounded-lg">
                            <span className="block text-[8px] text-gray-500">{t('seasonalMealPlanFat', 'چربی')}</span>
                            <strong className="text-rose-900 dark:text-rose-200">{toPersianDigits(defaultSnackFat)}g</strong>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Bottom Action buttons */}
                  <div className="pt-2 border-t border-gray-100 dark:border-gray-800 flex flex-wrap items-center justify-between gap-1.5">
                    <button
                      onClick={() => {
                        setAiTargetDay(dayMeal);
                        setAiUserPrompt(t('seasonalMealPlanAiPromptAlternative', 'یک جایگزین کم‌کالری یا سالم‌تر برای {{foodName}} و میان‌وعده پیشنهاد بده.', { foodName: locLunch.name }));
                        setAiResponse('');
                        setAiModalOpen(true);
                      }}
                      className="px-2.5 py-1.5 rounded-xl bg-purple-50 dark:bg-purple-950/50 hover:bg-purple-100 text-purple-700 dark:text-purple-300 font-bold text-[10px] flex items-center gap-1 transition-all cursor-pointer"
                    >
                      <Sparkles className="w-3 h-3 text-purple-500" />
                      <span>{t('seasonalMealPlanAiConsulting', 'مشاوره AI')}</span>
                    </button>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleLogMealToTodayRecord(dayMeal, 'lunch')}
                        className="px-2 py-1 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-[10px] flex items-center gap-1 shadow-sm transition-all cursor-pointer"
                        title={t('seasonalMealPlanLogLunchTitle', 'ثبت ناهار در دفترچه تغذیه')}
                      >
                        <Utensils className="w-3 h-3" />
                        <span>{t('seasonalMealPlanLogLunch', 'ثبت ناهار')}</span>
                      </button>

                      <button
                        onClick={() => handleLogMealToTodayRecord(dayMeal, 'snack')}
                        className="px-2 py-1 rounded-xl bg-amber-500 hover:bg-amber-400 text-gray-950 font-bold text-[10px] flex items-center gap-1 shadow-sm transition-all cursor-pointer"
                        title={t('seasonalMealPlanLogSnackTitle', 'ثبت میان‌وعده در دفترچه تغذیه')}
                      >
                        <Coffee className="w-3 h-3" />
                        <span>{t('seasonalMealPlanLogSnack', 'ثبت میان‌وعده')}</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* VIEW: HEALTH HABITS SECTION (NEW ROUTINE & COFFEE BENEFITS) */}
      {activeTab === 'habits' && (
        <div className="space-y-6">
          {/* FEATURED HABIT CARD: GLP-1 HEALTH COFFEE */}
          <div className="bg-gradient-to-br from-amber-900 via-stone-900 to-emerald-950 text-white p-6 sm:p-8 rounded-3xl shadow-xl border border-amber-800/40 relative overflow-hidden space-y-4">
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
            
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-500/30 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-500/20 rounded-2xl border border-amber-400/30 text-amber-300">
                  <Coffee className="w-7 h-7" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-amber-400 text-gray-950">
                      {t('seasonalMealPlanDailyProtocol', 'پروتکل ویژه روزانه')}
                    </span>
                    <span className="text-xs text-amber-200">{t('seasonalMealPlanGLP1Stimulation', 'تحریک طبیعی هورمون‌های GLP-1 & GIP')}</span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-black text-white mt-0.5">
                    {t('seasonalMealPlanCoffeeTitle', 'عادت روزانه: قهوه سلامتی (کولد برو / اسپرسو + گردو و چیا)')}
                  </h2>
                </div>
              </div>

              <button
                onClick={() => handleToggleHabit('habit_coffee_glp1')}
                className={`px-4 py-2.5 rounded-2xl font-black text-xs sm:text-sm flex items-center gap-2 shadow-lg transition-all cursor-pointer ${
                  completedHabits[`${todayStr}_habit_coffee_glp1`]
                    ? 'bg-emerald-500 text-white ring-2 ring-emerald-400'
                    : 'bg-amber-500 hover:bg-amber-400 text-gray-950'
                }`}
              >
                {completedHabits[`${todayStr}_habit_coffee_glp1`] ? (
                  <>
                    <CheckSquare className="w-4 h-4" />
                    <span>{t('seasonalMealPlanCompletedToday', 'تکمیل شده برای امروز ✨')}</span>
                  </>
                ) : (
                  <>
                    <Square className="w-4 h-4" />
                    <span>{t('seasonalMealPlanLogCoffeeToday', 'ثبت مصرف قهوه امروز ☕')}</span>
                  </>
                )}
              </button>
            </div>

            {/* Preparation Recipe & Macros */}
            {(() => {
              const localizedCoffeeHabit = getLocalizedHabit(DEFAULT_HEALTH_HABITS[0], lang);
              return (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2 bg-black/30 backdrop-blur-md p-4 rounded-2xl border border-white/10 space-y-2">
                      <h4 className="font-bold text-xs text-amber-300 flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>{t('seasonalMealPlanCoffeeRecipeTitle', 'طرز تهیه و ترکیب طلایی قهوه سلامتی:')}</span>
                      </h4>
                      <p className="text-xs text-amber-100/90 leading-relaxed">
                        {localizedCoffeeHabit.howToPrepare}
                      </p>
                    </div>

                    <div className="bg-black/30 backdrop-blur-md p-4 rounded-2xl border border-white/10 space-y-2 text-xs">
                      <span className="font-bold text-emerald-300 block">📊 {t('seasonalMealPlanCoffeeNutritionInfo', 'ارزش تغذیه‌ای این نوشیدنی:')}</span>
                      <div className="grid grid-cols-2 gap-2 font-mono">
                        <div className="bg-white/10 p-2 rounded-xl text-center">
                          <span className="block text-[9px] text-gray-400">{t('seasonalMealPlanCalorie', 'کالری')}</span>
                          <strong className="text-amber-300 text-sm">{toPersianDigits(210)} kcal</strong>
                        </div>
                        <div className="bg-white/10 p-2 rounded-xl text-center">
                          <span className="block text-[9px] text-gray-400">{t('seasonalMealPlanProtein', 'پروتئین')}</span>
                          <strong className="text-emerald-300 text-sm">{toPersianDigits(16)} g</strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Health Benefits List */}
                  <div className="space-y-2 pt-1">
                    <h4 className="font-bold text-xs text-emerald-300 flex items-center gap-1.5">
                      <Heart className="w-3.5 h-3.5" />
                      <span>{t('seasonalMealPlanCoffeeBenefits', 'فواید شگفت‌انگیز و اثبات‌شده علمی:')}</span>
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      {localizedCoffeeHabit.benefits.map((b, idx) => (
                        <div key={idx} className="bg-white/5 p-2.5 rounded-xl border border-white/5 flex items-start gap-2">
                          <span className="text-amber-400 font-bold">•</span>
                          <span className="text-gray-200">{b}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>

          {/* ALL HEALTH HABITS LIST */}
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 border border-gray-200 dark:border-gray-800 shadow-sm space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-800 pb-4">
              <div>
                <h3 className="text-lg font-black text-gray-900 dark:text-white flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-emerald-600" />
                  <span>{t('seasonalMealPlanChecklistTitle', 'چک‌لیست عادات متابولیک و پروتکل‌های روزانه')}</span>
                </h3>
                <p className="text-xs text-gray-500">{t('seasonalMealPlanChecklistDesc', 'انجام روزانه این عادات، سرعت سوزاندن چربی و پایداری قند خون را ۲ برابر می‌کند.')}</p>
              </div>

              <button
                onClick={() => setNewHabitModalOpen(true)}
                className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center gap-1.5 shadow transition-all cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>{t('seasonalMealPlanAddCustomHabit', 'افزودن عادت شخصی جدید')}</span>
              </button>
            </div>

            {/* Habits Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {allHabitsList.map((habit) => {
                const isChecked = Boolean(completedHabits[`${todayStr}_${habit.id}`]);
                const locHabit = getLocalizedHabit(habit, lang);

                return (
                  <div
                    key={habit.id}
                    className={`p-5 rounded-3xl border transition-all flex flex-col justify-between space-y-3 ${
                      isChecked
                        ? 'bg-emerald-50/80 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-800'
                        : 'bg-gray-50/60 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2.5">
                          <span className="text-2xl p-2 bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800">
                            {habit.icon}
                          </span>
                          <div>
                            <h4 className="font-black text-sm text-gray-900 dark:text-white">
                              {locHabit.title}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {locHabit.shortDesc}
                            </p>
                          </div>
                        </div>

                        {habit.isCustom && (
                          <button
                            onClick={() => handleRemoveCustomHabit(habit.id)}
                            className="p-1.5 text-gray-400 hover:text-rose-500 rounded-lg transition"
                            title={t('seasonalMealPlanDeleteHabitTooltip', 'حذف عادت')}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      {/* Benefits list */}
                      <div className="bg-white/70 dark:bg-gray-900/60 p-3 rounded-2xl border border-gray-100 dark:border-gray-800 space-y-1 text-xs">
                        <span className="text-[10px] font-bold text-gray-400 block">{t('seasonalMealPlanKeyBenefitsLabel', 'فواید کلیدی:')}</span>
                        <ul className="space-y-1">
                          {locHabit.benefits.map((b, idx) => (
                            <li key={idx} className="flex items-start gap-1.5 text-gray-700 dark:text-gray-300">
                              <span className="text-emerald-500 font-bold shrink-0">•</span>
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Action button */}
                    <div className="pt-2 border-t border-gray-200/60 dark:border-gray-700/60 flex items-center justify-between">
                      <span className="text-[11px] text-gray-500">
                        {t('seasonalMealPlanTodayStatus', 'وضعیت امروز:')} <strong className={isChecked ? 'text-emerald-600' : 'text-gray-400'}>{isChecked ? t('seasonalMealPlanStatusDone', 'انجام شد ✨') : t('seasonalMealPlanStatusPending', 'هنوز انجام نشده')}</strong>
                      </span>

                      <button
                        onClick={() => handleToggleHabit(habit.id)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                          isChecked
                            ? 'bg-emerald-600 text-white shadow-sm'
                            : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {isChecked ? (
                          <>
                            <CheckSquare className="w-3.5 h-3.5" />
                            <span>{t('seasonalMealPlanMarkedDone', 'تکمیل شد')}</span>
                          </>
                        ) : (
                          <>
                            <Square className="w-3.5 h-3.5" />
                            <span>{t('seasonalMealPlanLogToday', 'ثبت امروز')}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* VIEW 2: WEEKLY SHOPPING LIST */}
      {activeTab === 'shopping' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 border border-gray-200 dark:border-gray-800 shadow-sm space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-800 pb-4">
              <div>
                <h3 className="text-lg font-black text-gray-900 dark:text-white flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-emerald-600" />
                  <span>{t('seasonalMealPlanShoppingListTitle', 'لیست خرید هفتگی {{seasonName}}', { seasonName: currentSeason.seasonName })}</span>
                </h3>
                <p className="text-xs text-gray-500">{t('seasonalMealPlanShoppingListDesc', 'مواد اولیه کلیدی جهت تامین فیبر، چربی‌های مفید و ترکیبات تلخ این فصل')}</p>
              </div>

              <button
                onClick={() => {
                  const allItems = [
                    ...currentSeason.shoppingList.proteins.map(p => getLocalizedShoppingItem(p, lang)),
                    ...currentSeason.shoppingList.grainsLegumes.map(g => getLocalizedShoppingItem(g, lang)),
                    ...currentSeason.shoppingList.vegetables.map(v => getLocalizedShoppingItem(v, lang)),
                    ...currentSeason.shoppingList.fatsSpices.map(f => getLocalizedShoppingItem(f, lang))
                  ].join('\n• ');
                  handleCopyText(`${t('seasonalMealPlanShoppingListCopyPrefix', 'لیست خرید')} (${seasonName})\n• ${allItems}`, t('seasonalMealPlanShoppingListCopyPrefix', 'لیست خرید'));
                }}
                className="px-3.5 py-2 rounded-xl bg-emerald-600 text-white font-bold text-xs flex items-center gap-1.5 shadow transition-all cursor-pointer"
              >
                <Copy className="w-4 h-4" />
                <span>{t('seasonalMealPlanCopyAllList', 'کپی کل لیست خرید')}</span>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Category 1: Proteins */}
              <div className="bg-amber-50/60 dark:bg-amber-950/20 p-4 rounded-2xl border border-amber-200/60 dark:border-amber-800/40 space-y-2">
                <h4 className="font-bold text-sm text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                  <span>🍗 {t('seasonalMealPlanCatProtein', 'منابع پروتئینی و لبنی')}</span>
                </h4>
                <ul className="space-y-1.5 text-xs text-amber-950 dark:text-amber-100">
                  {currentSeason.shoppingList.proteins.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                      <span>{getLocalizedShoppingItem(item, lang)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Category 2: Grains & Legumes */}
              <div className="bg-blue-50/60 dark:bg-blue-950/20 p-4 rounded-2xl border border-blue-200/60 dark:border-blue-800/40 space-y-2">
                <h4 className="font-bold text-sm text-blue-900 dark:text-blue-200 flex items-center gap-1.5">
                  <span>🌾 {t('seasonalMealPlanCatGrains', 'برنج و حبوبات (فیبر بالا)')}</span>
                </h4>
                <ul className="space-y-1.5 text-xs text-blue-950 dark:text-blue-100">
                  {currentSeason.shoppingList.grainsLegumes.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                      <span>{getLocalizedShoppingItem(item, lang)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Category 3: Vegetables & Herbs */}
              <div className="bg-emerald-50/60 dark:bg-emerald-950/20 p-4 rounded-2xl border border-emerald-200/60 dark:border-emerald-800/40 space-y-2">
                <h4 className="font-bold text-sm text-emerald-900 dark:text-emerald-200 flex items-center gap-1.5">
                  <span>🥗 {t('seasonalMealPlanCatVeg', 'سبزیجات و صیفی‌جات تلخ')}</span>
                </h4>
                <ul className="space-y-1.5 text-xs text-emerald-950 dark:text-emerald-100">
                  {currentSeason.shoppingList.vegetables.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                      <span>{getLocalizedShoppingItem(item, lang)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Category 4: Fats & Spices */}
              <div className="bg-purple-50/60 dark:bg-purple-950/20 p-4 rounded-2xl border border-purple-200/60 dark:border-purple-800/40 space-y-2">
                <h4 className="font-bold text-sm text-purple-900 dark:text-purple-200 flex items-center gap-1.5">
                  <span>🥑 {t('seasonalMealPlanCatFat', 'چربی‌های مفید و ادویه‌جات')}</span>
                </h4>
                <ul className="space-y-1.5 text-xs text-purple-950 dark:text-purple-100">
                  {currentSeason.shoppingList.fatsSpices.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 shrink-0" />
                      <span>{getLocalizedShoppingItem(item, lang)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW 3: MONTHLY SUMMARY */}
      {activeTab === 'summary' && (
        <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 border border-gray-200 dark:border-gray-800 shadow-sm space-y-4">
          <h3 className="text-lg font-black text-gray-900 dark:text-white flex items-center gap-2 border-b pb-3 border-gray-100 dark:border-gray-800">
            <Layers className="w-5 h-5 text-emerald-600" />
            <span>{t('seasonalMealPlanMonthlySummaryTitle', 'خلاصه مواد مصرفی ماهانه ({{seasonName}})', { seasonName })}</span>
          </h3>

          <div className="bg-emerald-50/70 dark:bg-emerald-950/30 p-5 rounded-2xl border border-emerald-200 dark:border-emerald-800/50 space-y-2">
            <p className="text-sm font-bold text-emerald-950 dark:text-emerald-100 leading-relaxed">
              {monthlySummary}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-2xl space-y-1">
              <span className="text-xs text-gray-500 block">🥩 {t('seasonalMealPlanSummProtein', 'پروتئین قرمز و سفید:')}</span>
              <strong className="text-sm text-gray-900 dark:text-white">{t('seasonalMealPlanSummProteinVal', 'حدود ۳.۵ تا ۴ کیلوگرم')}</strong>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-2xl space-y-1">
              <span className="text-xs text-gray-500 block">🫘 {t('seasonalMealPlanSummLegumes', 'انواع حبوبات و فیبر:')}</span>
              <strong className="text-sm text-gray-900 dark:text-white">{t('seasonalMealPlanSummLegumesVal', 'حدود ۳ تا ۴ کیلوگرم')}</strong>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-2xl space-y-1">
              <span className="text-xs text-gray-500 block">🫒 {t('seasonalMealPlanSummOliveOil', 'روغن زیتون فرابکر:')}</span>
              <strong className="text-sm text-gray-900 dark:text-white">{t('seasonalMealPlanSummOliveOilVal', '۲ تا ۴ بطری برای کل خانواده')}</strong>
            </div>
          </div>
        </div>
      )}

      {/* VIEW 4: NUTRITIONAL ANALYSIS */}
      {activeTab === 'nutrition' && (
        <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 border border-gray-200 dark:border-gray-800 shadow-sm space-y-4">
          <h3 className="text-lg font-black text-gray-900 dark:text-white flex items-center gap-2 border-b pb-3 border-gray-100 dark:border-gray-800">
            <Heart className="w-5 h-5 text-rose-600" />
            <span>{t('seasonalMealPlanNutritionAnalysisTitle', 'آنالیز تغذیه‌ای و مکانیزم لاغری GLP-1 ({{seasonName}})', { seasonName })}</span>
          </h3>

          <div className="p-5 bg-gradient-to-br from-amber-50 to-emerald-50 dark:from-amber-950/30 dark:to-emerald-950/30 rounded-2xl border border-emerald-200/60 dark:border-emerald-800/50 space-y-3">
            <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
              {nutritionalAnalysis}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 font-mono text-xs">
              <div className="bg-white/80 dark:bg-gray-900/80 p-3 rounded-xl border border-emerald-200 dark:border-emerald-800">
                <span className="block text-gray-500 text-[10px]">🍗 {t('seasonalMealPlanNutrProtein', 'سهم پروتئین')}</span>
                <strong className="text-emerald-700 dark:text-emerald-300 text-sm">{t('seasonalMealPlanNutrProteinVal', '۳۰٪ انرژی روزانه')}</strong>
              </div>
              <div className="bg-white/80 dark:bg-gray-900/80 p-3 rounded-xl border border-emerald-200 dark:border-emerald-800">
                <span className="block text-gray-500 text-[10px]">🌾 {t('seasonalMealPlanNutrCarb', 'کربوهیدرات پیچیده')}</span>
                <strong className="text-blue-700 dark:text-blue-300 text-sm">{t('seasonalMealPlanNutrCarbVal', '۴۰٪ (برنج کته محدود)')}</strong>
              </div>
              <div className="bg-white/80 dark:bg-gray-900/80 p-3 rounded-xl border border-emerald-200 dark:border-emerald-800">
                <span className="block text-gray-500 text-[10px]">🥑 {t('seasonalMealPlanNutrFat', 'چربی مفید')}</span>
                <strong className="text-rose-700 dark:text-rose-300 text-sm">{t('seasonalMealPlanNutrFatVal', '۳۰٪ (زیتون و گردو)')}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW 5: TIME SAVING MEAL PREP TIPS */}
      {activeTab === 'tips' && (
        <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 border border-gray-200 dark:border-gray-800 shadow-sm space-y-4">
          <h3 className="text-lg font-black text-gray-900 dark:text-white flex items-center gap-2 border-b pb-3 border-gray-100 dark:border-gray-800">
            <Clock className="w-5 h-5 text-amber-500" />
            <span>{t('seasonalMealPlanPrepTipsTitle', 'ترفندهای صرفه‌جویی در زمان و آماده‌سازی سریع (Meal Prep)')}</span>
          </h3>

          <div className="space-y-3">
            {timeSavingTips.map((tip, idx) => (
              <div key={idx} className="bg-amber-50/50 dark:bg-amber-950/20 p-4 rounded-2xl border border-amber-200/50 dark:border-amber-800/40 flex items-start gap-3">
                <div className="w-7 h-7 rounded-xl bg-amber-500 text-white font-bold flex items-center justify-center shrink-0 text-xs">
                  {toPersianDigits(idx + 1)}
                </div>
                <p className="text-xs sm:text-sm text-gray-800 dark:text-gray-200 font-medium leading-relaxed pt-1">
                  {tip}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* EDITING DAY MEAL MODAL (LUNCH & SNACK) */}
      {editingDay && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 max-w-xl w-full border border-gray-200 dark:border-gray-800 shadow-2xl space-y-4 my-8">
            <div className="flex items-center justify-between border-b pb-3 border-gray-100 dark:border-gray-800">
              <h3 className="text-base font-black text-gray-900 dark:text-white flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-emerald-600" />
                <span>{t('seasonalMealPlanEditDayTitle', 'ویرایش روز {{dayName}} (ناهار و میان‌وعده)', { dayName: editingDay.dayName })}</span>
              </h3>
              <button onClick={() => setEditingDay(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs max-h-[70vh] overflow-y-auto pr-1">
              {/* LUNCH EDIT SECTION */}
              <div className="bg-emerald-50/50 dark:bg-emerald-950/20 p-4 rounded-2xl border border-emerald-200/60 dark:border-emerald-800 space-y-3">
                <h4 className="font-black text-xs text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
                  <Utensils className="w-4 h-4" />
                  <span>{t('seasonalMealPlanLunchSettings', 'تنظیمات وعده ناهار')}</span>
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-gray-600 dark:text-gray-400 font-bold mb-1">{t('seasonalMealPlanFoodNameLabel', 'نام غذا:')}</label>
                    <input
                      type="text"
                      value={editingDay.foodName}
                      onChange={e => setEditingDay({ ...editingDay, foodName: e.target.value })}
                      className="w-full p-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 dark:text-gray-400 font-bold mb-1">{t('seasonalMealPlanPortionSizeLabel', 'اندازه سهم:')}</label>
                    <input
                      type="text"
                      value={editingDay.portionSize}
                      onChange={e => setEditingDay({ ...editingDay, portionSize: e.target.value })}
                      className="w-full p-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-600 dark:text-gray-400 font-bold mb-1">{t('seasonalMealPlanIngredientsLabel', 'ترکیبات اصلی:')}</label>
                  <input
                    type="text"
                    value={editingDay.ingredients}
                    onChange={e => setEditingDay({ ...editingDay, ingredients: e.target.value })}
                    className="w-full p-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                  />
                </div>

                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <label className="block text-gray-500 text-[10px]">{t('seasonalMealPlanCalorie', 'کالری:')}</label>
                    <input
                      type="number"
                      value={editingDay.calories}
                      onChange={e => setEditingDay({ ...editingDay, calories: Number(e.target.value) })}
                      className="w-full p-1.5 bg-white dark:bg-gray-800 rounded-xl border text-center font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-500 text-[10px]">{t('seasonalMealPlanProteinLabel', 'پروتئین (g):')}</label>
                    <input
                      type="number"
                      value={editingDay.protein}
                      onChange={e => setEditingDay({ ...editingDay, protein: Number(e.target.value) })}
                      className="w-full p-1.5 bg-white dark:bg-gray-800 rounded-xl border text-center font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-500 text-[10px]">{t('seasonalMealPlanCarbLabel', 'کربو (g):')}</label>
                    <input
                      type="number"
                      value={editingDay.carbs}
                      onChange={e => setEditingDay({ ...editingDay, carbs: Number(e.target.value) })}
                      className="w-full p-1.5 bg-white dark:bg-gray-800 rounded-xl border text-center font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-500 text-[10px]">{t('seasonalMealPlanFatLabel', 'چربی (g):')}</label>
                    <input
                      type="number"
                      value={editingDay.fat}
                      onChange={e => setEditingDay({ ...editingDay, fat: Number(e.target.value) })}
                      className="w-full p-1.5 bg-white dark:bg-gray-800 rounded-xl border text-center font-mono"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-600 dark:text-gray-400 font-bold mb-1">{t('seasonalMealPlanSideDishLabel', 'دورچین توصیه شده:')}</label>
                  <input
                    type="text"
                    value={editingDay.sideDishes}
                    onChange={e => setEditingDay({ ...editingDay, sideDishes: e.target.value })}
                    className="w-full p-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                  />
                </div>
              </div>

              {/* SNACK EDIT SECTION */}
              <div className="bg-amber-50/50 dark:bg-amber-950/20 p-4 rounded-2xl border border-amber-200/60 dark:border-amber-800 space-y-3">
                <h4 className="font-black text-xs text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                  <Coffee className="w-4 h-4" />
                  <span>{t('seasonalMealPlanSnackSettings', 'تنظیمات وعده میان‌وعده')}</span>
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-gray-600 dark:text-gray-400 font-bold mb-1">{t('seasonalMealPlanSnackNameLabel', 'نام میان‌وعده:')}</label>
                    <input
                      type="text"
                      value={editingDay.snackName || 'قهوه سلامتی (کولد برو) + ماست یونانی و دانه چیا'}
                      onChange={e => setEditingDay({ ...editingDay, snackName: e.target.value })}
                      className="w-full p-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 dark:text-gray-400 font-bold mb-1">{t('seasonalMealPlanPortionSizeLabel', 'اندازه سهم:')}</label>
                    <input
                      type="text"
                      value={editingDay.snackPortionSize || '۱ لیوان (۲۵۰ml)'}
                      onChange={e => setEditingDay({ ...editingDay, snackPortionSize: e.target.value })}
                      className="w-full p-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-600 dark:text-gray-400 font-bold mb-1">{t('seasonalMealPlanSnackIngredientsLabel', 'ترکیبات میان‌وعده:')}</label>
                  <input
                    type="text"
                    value={editingDay.snackIngredients || 'قهوه سرد + ۱۵۰گ ماست یونانی + ۱۰گ گردو + ۱ ق‌چ دانه چیا + زردچوبه و روغن زیتون'}
                    onChange={e => setEditingDay({ ...editingDay, snackIngredients: e.target.value })}
                    className="w-full p-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                  />
                </div>

                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <label className="block text-gray-500 text-[10px]">{t('seasonalMealPlanCalorie', 'کالری:')}</label>
                    <input
                      type="number"
                      value={editingDay.snackCalories || 210}
                      onChange={e => setEditingDay({ ...editingDay, snackCalories: Number(e.target.value) })}
                      className="w-full p-1.5 bg-white dark:bg-gray-800 rounded-xl border text-center font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-500 text-[10px]">{t('seasonalMealPlanProteinLabel', 'پروتئین (g):')}</label>
                    <input
                      type="number"
                      value={editingDay.snackProtein || 16}
                      onChange={e => setEditingDay({ ...editingDay, snackProtein: Number(e.target.value) })}
                      className="w-full p-1.5 bg-white dark:bg-gray-800 rounded-xl border text-center font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-500 text-[10px]">{t('seasonalMealPlanCarbLabel', 'کربو (g):')}</label>
                    <input
                      type="number"
                      value={editingDay.snackCarbs || 10}
                      onChange={e => setEditingDay({ ...editingDay, snackCarbs: Number(e.target.value) })}
                      className="w-full p-1.5 bg-white dark:bg-gray-800 rounded-xl border text-center font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-500 text-[10px]">{t('seasonalMealPlanFatLabel', 'چربی (g):')}</label>
                    <input
                      type="number"
                      value={editingDay.snackFat || 12}
                      onChange={e => setEditingDay({ ...editingDay, snackFat: Number(e.target.value) })}
                      className="w-full p-1.5 bg-white dark:bg-gray-800 rounded-xl border text-center font-mono"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
              <button
                onClick={() => setEditingDay(null)}
                className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300 font-bold"
              >
                {t('commonCancel', 'انصراف')}
              </button>
              <button
                onClick={() => handleSaveDayEdit(editingDay)}
                className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold shadow-md"
              >
                {t('seasonalMealPlanSaveToCloud', 'ذخیره تغییرات در دیتابیس ابری')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEW CUSTOM HABIT MODAL */}
      {newHabitModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 max-w-md w-full border border-gray-200 dark:border-gray-800 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b pb-3 border-gray-100 dark:border-gray-800">
              <h3 className="text-base font-black text-gray-900 dark:text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-emerald-600" />
                <span>{t('seasonalMealPlanDefineNewHabit', 'تعریف عادت سلامت جدید')}</span>
              </h3>
              <button onClick={() => setNewHabitModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block font-bold text-gray-700 dark:text-gray-300 mb-1">{t('seasonalMealPlanHabitTitleLabel', 'عنوان عادت:')}</label>
                <input
                  type="text"
                  placeholder={t('seasonalMealPlanHabitTitlePlaceholder', 'مثلاً: پیاده‌روی ۲۰ دقیقه‌ای بعد از ناهار')}
                  value={newHabitTitle}
                  onChange={e => setNewHabitTitle(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 dark:text-gray-300 mb-1">{t('seasonalMealPlanHabitDescLabel', 'توضیح مختصر:')}</label>
                <input
                  type="text"
                  placeholder={t('seasonalMealPlanHabitDescPlaceholder', 'مثلاً: پیاده‌روی برای کاهش سریع قند خون بعد از غذا')}
                  value={newHabitDesc}
                  onChange={e => setNewHabitDesc(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 dark:text-gray-300 mb-1">{t('seasonalMealPlanHabitBenefitLabel', 'مزیت اصلی برای سلامتی:')}</label>
                <textarea
                  rows={2}
                  placeholder={t('seasonalMealPlanHabitBenefitPlaceholder', 'مثلاً: افزایش حساسیت به انسولین و جلوگیری از خواب‌آلودگی بعد از غذا')}
                  value={newHabitBenefit}
                  onChange={e => setNewHabitBenefit(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
              <button
                onClick={() => setNewHabitModalOpen(false)}
                className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 font-bold"
              >
                {t('commonCancel', 'انصراف')}
              </button>
              <button
                onClick={handleAddCustomHabit}
                disabled={!newHabitTitle.trim()}
                className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold shadow-md"
              >
                {t('seasonalMealPlanSaveHabitToCloud', 'ثبت عادت در دیتابیس ابری')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI ASSISTANT MODAL */}
      {aiModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 max-w-lg w-full border border-gray-200 dark:border-gray-800 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b pb-3 border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500 animate-pulse" />
                <h3 className="text-base font-black text-gray-900 dark:text-white">
                  {t('seasonalMealPlanAiAssistantTitle', 'دستیار هوش مصنوعی هوشمند تغذیه GLP-1')}
                </h3>
              </div>
              <button onClick={() => setAiModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-gray-500">
              {t('seasonalMealPlanAiAssistantDesc', 'بر اساس ۵ ترفند علمی تغذیه و سلامت متابولیک جهت بهینه‌سازی برنامه غذایی، جایگزینی مواد یا دستور پخت‌های سالم.')}
            </p>

            {aiTargetDay && (() => {
              const locDayFood = getLocalizedFoodItem(aiTargetDay.foodName, aiTargetDay.portionSize, lang);
              return (
                <div className="bg-emerald-50 dark:bg-emerald-950/40 p-3 rounded-2xl border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-900 dark:text-emerald-200">
                  <strong>{t('seasonalMealPlanSelectedFood', 'غذا انتخاب‌شده:')}</strong> {locDayFood.name} ({locDayFood.quantity})
                </div>
              );
            })()}

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300">
                  {t('seasonalMealPlanQuestionLabel', 'سوال یا درخواست تغییر:')}
                </label>
                <VoiceInputButton
                  currentValue={aiUserPrompt}
                  onUpdate={(val) => setAiUserPrompt(val)}
                  disabled={isAiLoading}
                  title={t('voiceInputNormal', 'ورودی صوتی')}
                />
              </div>
              <textarea
                rows={3}
                placeholder={t('seasonalMealPlanAiPromptPlaceholder', 'مثلاً: قهوه کولد برو چه کالری و فایده‌ای داره یا جایگزین سالم‌تر برای این وعده بگو...')}
                value={aiUserPrompt}
                onChange={e => setAiUserPrompt(e.target.value)}
                className="w-full p-3 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 text-xs focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <button
              onClick={handleRunAiAssistant}
              disabled={isAiLoading || !aiUserPrompt.trim()}
              className="w-full py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-amber-500 hover:from-emerald-500 hover:to-amber-400 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 cursor-pointer"
            >
              <Sparkles className="w-4 h-4 text-amber-300" />
              <span>{isAiLoading ? t('seasonalMealPlanAiLoading', 'در حال دریافت تحلیل و دستور از AI...') : t('seasonalMealPlanAiGetAdvice', 'دریافت مشاوره از هوش مصنوعی')}</span>
            </button>

            {aiResponse && (
              <div className="bg-emerald-50/40 dark:bg-gray-800/90 p-4 rounded-2xl border border-emerald-200 dark:border-gray-700 space-y-3 text-xs leading-relaxed text-gray-800 dark:text-gray-200 max-h-96 overflow-y-auto shadow-inner">
                <div className="flex items-center justify-between border-b border-emerald-200 dark:border-gray-700 pb-2 mb-2">
                  <span className="font-black text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5 text-xs">
                    <Sparkles className="w-4 h-4 text-emerald-500" />
                    {t('seasonalMealPlanAiAnalysisHeader', 'تحلیل و راهنمایی هوشمند هوش مصنوعی:')}
                  </span>
                  <button
                    onClick={() => handleCopyText(aiResponse, t('seasonalMealPlanAiResponse', 'پاسخ هوش مصنوعی'))}
                    className="text-[11px] font-bold text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 bg-white dark:bg-gray-900 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm transition"
                  >
                    {t('commonCopy', 'کپی متن')}
                  </button>
                </div>
                <FormattedAiResponse content={aiResponse} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// HELPER COMPONENTS FOR BEAUTIFUL PARSING AND FORMATTING AI MARKDOWN
// ----------------------------------------------------------------------
function parseInlineFormatting(text: string): React.ReactNode {
  if (!text) return text;
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-black text-emerald-800 dark:text-emerald-300 bg-emerald-100/60 dark:bg-emerald-950/60 px-1 py-0.5 rounded text-emerald-900 dark:text-emerald-200">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
}

function FormattedAiResponse({ content }: { content: string }) {
  if (!content) return null;

  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Table detection: line starting with '|' and next line having '---'
    if (line.trim().startsWith('|') && i + 1 < lines.length && lines[i + 1].includes('---')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }

      if (tableLines.length >= 2) {
        const headerRow = tableLines[0].split('|').map(s => s.trim()).filter(Boolean);
        const bodyRows = tableLines.slice(2).map(r => r.split('|').map(s => s.trim()).filter(Boolean));

        elements.push(
          <div key={`table-${i}`} className="my-3 overflow-x-auto rounded-xl border border-emerald-300 dark:border-emerald-800 shadow-md bg-white dark:bg-gray-900/90">
            <table className="w-full text-xs text-right border-collapse">
              <thead>
                <tr className="bg-emerald-600 text-white border-b border-emerald-700 font-bold">
                  {headerRow.map((cell, cIdx) => (
                    <th key={cIdx} className="px-3 py-2 text-right">{parseInlineFormatting(cell)}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {bodyRows.map((row, rIdx) => (
                  <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-transparent' : 'bg-emerald-50/50 dark:bg-gray-800/40'}>
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} className="px-3 py-2 text-gray-800 dark:text-gray-200">{parseInlineFormatting(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        continue;
      }
    }

    // Headings
    if (line.trim().startsWith('#')) {
      const headingText = line.replace(/^#+\s*/, '').trim();
      elements.push(
        <div key={`h-${i}`} className="mt-4 mb-2 pb-1 border-b border-emerald-200 dark:border-emerald-800 flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-sm"></div>
          <h4 className="font-black text-emerald-900 dark:text-emerald-200 text-xs sm:text-sm">
            {parseInlineFormatting(headingText)}
          </h4>
        </div>
      );
      i++;
      continue;
    }

    // Bullet points (- or * or •)
    const bulletMatch = line.trim().match(/^([-*•]|\d+\.)\s+(.*)/);
    if (bulletMatch) {
      const bulletContent = bulletMatch[2];
      elements.push(
        <div key={`bullet-${i}`} className="flex items-start gap-2 my-1 pr-1">
          <span className="text-emerald-600 dark:text-emerald-400 font-black text-sm leading-none mt-0.5">•</span>
          <div className="flex-1 text-gray-800 dark:text-gray-200 text-xs leading-relaxed">
            {parseInlineFormatting(bulletContent)}
          </div>
        </div>
      );
      i++;
      continue;
    }

    // Horizontal line
    if (line.trim() === '---' || line.trim() === '***') {
      elements.push(<hr key={`hr-${i}`} className="my-3 border-emerald-200 dark:border-emerald-900" />);
      i++;
      continue;
    }

    // Empty line
    if (!line.trim()) {
      elements.push(<div key={`empty-${i}`} className="h-1.5"></div>);
      i++;
      continue;
    }

    // Standard text line
    elements.push(
      <p key={`p-${i}`} className="my-1 text-gray-800 dark:text-gray-200 text-xs leading-relaxed">
        {parseInlineFormatting(line)}
      </p>
    );
    i++;
  }

  return <div className="space-y-0.5 text-right font-sans">{elements}</div>;
}
